import { getCodeableConceptCode, isReferenceOf } from '@bariendo/lib/services';
import { SCHEDULING_PARAMETERS_EXTENSION, APPOINTMENT_CUSTOM_INFO_EXTENSION } from '@bariendo/lib/extensions';
import { getExtensionValue, getReferenceString, isResource, MedplumClient, resolveId } from '@medplum/core';
import {
  ActivityDefinition,
  Appointment,
  Coding,
  Encounter,
  Organization,
  Patient,
  Practitioner,
  Reference,
  Resource,
  Location,
} from '@medplum/fhirtypes';
import {
  AppointmentEvent,
  AppointmentFormData,
  AppointmentRepeatingInterval,
  AppointmentWithEncounter,
  GuestFormData,
} from '../types';
import { DEFAULT_MONTHLY_RECURRENCE_WEEKDAY } from './appointment';
import { AMBULATORY_ENCOUNTER_CLASS_CODING, VIRTUAL_ENCOUNTER_CLASS_CODING } from '@bariendo/lib/codings';
import {
  extractPatientsFromAppointment,
  extractLocationFromAppointment,
  extractPractitionersFromAppointment,
  extractVirtualServiceUrlFromAppointment,
  extractVirtualServicePhoneNumberFromAppointment,
  extractVirtualServiceAdminUrlFromAppointment,
  extractPartnerFromAppointment,
  extractContactTypeFromAppointment,
  extractActorsFromAppointment,
  isAppointmentRescheduled,
} from '@bariendo/lib/scheduling';
import { AppointmentStatus, IV_THERAPY_APPOINTMENT_TYPE_CODE } from '@bariendo/lib/constants';
import { canSearchResourceType } from '@/services/permissions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getLocationTimezone } from './availability';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Appointment statuses that should be shown on the calendar.
 * Excludes cancelled and entered-in-error.
 */
export const VALID_CALENDAR_APPOINTMENT_STATUSES: readonly string[] = [
  AppointmentStatus.BOOKED,
  AppointmentStatus.ARRIVED,
  AppointmentStatus.FULFILLED,
  AppointmentStatus.NOSHOW,
  AppointmentStatus.PENDING,
  AppointmentStatus.PROPOSED,
  AppointmentStatus.WAITLIST,
  AppointmentStatus.CHECKED_IN,
] as const;

export function isValidCalendarAppointmentStatus(status: string | undefined): boolean {
  return status !== undefined && VALID_CALENDAR_APPOINTMENT_STATUSES.includes(status);
}

/** Returns true if the appointment should be shown on the calendar (valid status or rescheduled/cancelled) */
export function isAppointmentVisibleOnCalendar(appointment: Appointment): boolean {
  return (
    isValidCalendarAppointmentStatus(appointment.status) ||
    (appointment.status === 'cancelled' && isAppointmentRescheduled(appointment))
  );
}

/**
 * Returns true if the appointment was rescheduled (has cancelationReason with SNOMED rescheduled code).
 * Lives in @bariendo/lib/scheduling so the bots package shares one implementation
 * (Customer.io appointment objects need the same distinction); re-exported here
 * to keep the existing call sites unchanged.
 */
export { isAppointmentRescheduled };

/** Returns true if the given status option is the current/selected state for the appointment */
export function isAppointmentStatusOptionSelected(appointment: Appointment, optionValue: string): boolean {
  const rescheduled = isAppointmentRescheduled(appointment);
  if (optionValue === 'rescheduled') {
    return rescheduled;
  }
  if (optionValue === 'cancelled') {
    return appointment.status === 'cancelled' && !rescheduled;
  }
  return appointment.status === optionValue;
}

export interface GetAppointmentsParams {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  _id?: string; // Appointment ID, used to fetch full appointment resource and update the calendar in real time
  practitioners?: string;
  roles?: string;
  status?: string;
  /** When provided, filter results by these status options (rescheduled/cancelled handled client-side) */
  statusFilter?: string[];
  locations?: string; // Comma-separated Location references (e.g. Location/1,Location/2)
}

/**
 * Maps FHIR Appointment status to a display-friendly status string.
 * When status is "cancelled", checks cancelationReason for rescheduled SNOMED code.
 */
export function getAppointmentStatusDisplay(appointment: Appointment): string {
  if (isAppointmentRescheduled(appointment)) {
    return 'Rescheduled';
  }
  const status = appointment.status;
  const mapper = {
    booked: 'Scheduled',
    arrived: 'Occurred',
    fulfilled: 'Occurred',
    cancelled: 'Cancelled',
    noshow: 'No show',
    pending: 'Scheduled',
    proposed: 'Scheduled',
    waitlist: 'Scheduled',
    'checked-in': 'Occurred',
    'entered-in-error': 'Cancelled',
  };
  return mapper[status ?? ''] ?? status?.toUpperCase() ?? 'UNKNOWN';
}

/**
 * The ways a booking can end without the visit happening. Rescheduled is split
 * out of cancelled: the visit moved rather than went away.
 */
export type AppointmentOutcome = 'cancelled' | 'rescheduled' | 'noshow';

export function getAppointmentOutcome(appointment: Appointment): AppointmentOutcome | undefined {
  if (appointment.status === 'cancelled') {
    return isAppointmentRescheduled(appointment) ? 'rescheduled' : 'cancelled';
  }
  if (appointment.status === 'noshow') {
    return 'noshow';
  }
  return undefined;
}

function getIdsFromReferences(references: Reference<Resource>[]): string {
  return references.map((ref) => resolveId(ref)).join(',');
}

async function getPartners(medplum: MedplumClient, appointments: Appointment[]) {
  const partnersRefs = new Set<Reference<Resource>>();

  for (const appointment of appointments) {
    const supportingInformation = appointment.supportingInformation ?? [];
    const partners = supportingInformation.filter((si) => isReferenceOf(si, 'Organization'));
    for (const partner of partners) {
      partnersRefs.add(partner);
    }
  }

  // Nothing to resolve; the old empty `_id=` search was a wasted round trip.
  if (partnersRefs.size === 0) {
    return [];
  }

  const partnerIds = getIdsFromReferences(Array.from(partnersRefs));
  const partners = await medplum.searchResources('Organization', { _id: partnerIds });

  return partners;
}

/**
 * Transforms a FHIR Appointment resource into an AppointmentEvent for the calendar.
 */
export function transformAppointmentToEvent(
  appointment: Appointment,
  getActivityDefinition?: (code: string | undefined) => ActivityDefinition | undefined
): AppointmentEvent {
  const appointmentTypeCode = appointment.appointmentType?.coding?.[0]?.code;
  const appointmentTypeDisplay = appointment.appointmentType?.coding?.[0]?.display;
  const activityDefinition = getActivityDefinition?.(appointmentTypeCode);

  // Use activity definition display name or appointment type display
  const title = activityDefinition?.code?.coding?.[0]?.display ?? appointmentTypeDisplay ?? 'Appointment';
  const location = (extractLocationFromAppointment(appointment) as Reference<Location>[])?.[0]?.display;

  return {
    start: appointment.start ? new Date(appointment.start) : new Date(),
    end: appointment.end ? new Date(appointment.end) : new Date(),
    title,
    appointmentResource: appointment,
    appointmentType: appointmentTypeDisplay ?? appointmentTypeCode,
    status: getAppointmentStatusDisplay(appointment),
    location,
    providers: extractPractitionersFromAppointment(appointment),
    patients: extractPatientsFromAppointment(appointment),
    contactType: extractContactTypeFromAppointment(appointment),
    videoCallUrl: extractVirtualServiceUrlFromAppointment(appointment),
    videoCallAdminUrl: extractVirtualServiceAdminUrlFromAppointment(appointment),
    virtualServicePhone: extractVirtualServicePhoneNumberFromAppointment(appointment),
    partner: extractPartnerFromAppointment(appointment),
    compartments: appointment.meta?.compartment,
  };
}

/**
 * Fetches appointments within a date range.
 * Optionally, fetches a specific appointment by ID.
 *
 * It includes the related resources with their full data, not only the references.
 */
export async function getAppointments(
  medplum: MedplumClient,
  params: GetAppointmentsParams
): Promise<AppointmentWithEncounter[]> {
  const { startDate, endDate } = params;

  // The chart is outside Patient Access's policy (BAR-2585) and an unlisted `_revinclude`
  // fails the whole search, so the Encounter join is only asked for by roles that can read it.
  const includeEncounters = canSearchResourceType(medplum, 'Encounter');

  const resources = await medplum.searchResources('Appointment', {
    ...(params._id && { _id: params._id }),
    ...(params.practitioners && { actor: params.practitioners }),
    ...(params.locations && { actor: params.locations }),
    ...(params.roles && {
      'slot:Slot.schedule:Schedule.actor:PractitionerRole.role': params.roles,
    }),
    // Fetch appointments that overlap the requested range:
    // start < endDate AND end > startDate
    date: `lt${endDate}`,
    end: `gt${startDate}`,
    _sort: 'date',
    _count: '500',
    status: params.status ?? [...VALID_CALENDAR_APPOINTMENT_STATUSES, AppointmentStatus.CANCELLED].join(','),
    ...(includeEncounters && { _revinclude: 'Encounter:appointment' }),
    _include: 'Appointment:practitioner', // Include practitioners that are associated with the appointments
    '_include:iterate': 'Appointment:patient', // Include patients that are associated with the appointments
  });

  const { appointments, encounters, practitioners, patients } = resources.reduce(
    (_resources, resource) => {
      const mapping = {
        Appointment: _resources.appointments,
        Encounter: _resources.encounters,
        Practitioner: _resources.practitioners,
        Patient: _resources.patients,
      };

      mapping[resource.resourceType].push(resource);

      return _resources;
    },
    {
      appointments: [] as Appointment[],
      encounters: [] as Encounter[],
      practitioners: [] as Practitioner[],
      patients: [] as Patient[],
    }
  );

  const partners = await getPartners(medplum, appointments);

  const partnerMap = new Map<string, Organization>();
  for (const p of partners) {
    partnerMap.set(getReferenceString(p) as string, p);
  }

  for (const appointment of appointments) {
    for (const participant of appointment.participant ?? []) {
      const ref = participant.actor?.reference;
      if (ref && participant.actor) {
        const practitioner = practitioners.find((p) => getReferenceString(p) === ref);
        const patient = patients.find((p) => getReferenceString(p) === ref);
        if (practitioner) {
          participant.actor.resource = practitioner;
        } else if (patient) {
          participant.actor.resource = patient;
        }
      }
    }

    for (const supportingInformation of appointment.supportingInformation ?? []) {
      const ref = supportingInformation.reference;
      if (ref) {
        const partner = partnerMap.get(ref);
        if (partner) {
          supportingInformation.resource = partner;
        }
      }
    }

    (appointment as AppointmentWithEncounter).encounter = encounters.find(
      (encounter) => getReferenceString(encounter.appointment?.[0] ?? {}) === getReferenceString(appointment)
    );
  }

  // When status filter is set, apply status-specific filtering (rescheduled vs cancelled)
  if (params.statusFilter && params.statusFilter.length > 0) {
    return appointments.filter((a) =>
      params.statusFilter!.some((optionValue) => isAppointmentStatusOptionSelected(a, optionValue))
    );
  }
  // Default: filter out cancelled appointments that are not rescheduled (only show rescheduled on calendar)
  return appointments.filter((a) => isAppointmentVisibleOnCalendar(a));
}

/**
 * Checks if the appointment description (notes) has any actual text content.
 * This field is filled by the TipTap editor and even if user don't input anything,
 * it will be filled with a default empty JSON structure.
 */
export function hasRichTextContent(content: unknown): boolean {
  if (!content || typeof content !== 'object') {
    return false;
  }

  const node = content as { type?: string; text?: string; content?: unknown[] };

  // If this node has text, check if it's non-empty
  if (node.type === 'text' && node.text) {
    return node.text.trim().length > 0;
  }

  // Recursively check children
  if (Array.isArray(node.content)) {
    return node.content.some((child) => hasRichTextContent(child));
  }

  return false;
}

// Shared with the finance-cancel-procedure bot (BAR-3033).
export { buildCancelAppointmentBundle } from '@bariendo/lib/scheduling';

export async function mapAppointmentToFormData(
  medplum: MedplumClient,
  appointment: Appointment
): Promise<AppointmentFormData> {
  const appointmentTypeCode = appointment.appointmentType?.coding?.[0]?.code;
  const activityDefinition = appointmentTypeCode
    ? await medplum.searchOne('ActivityDefinition', { code: appointmentTypeCode })
    : null;
  const isIvTherapy =
    activityDefinition != null && getCodeableConceptCode(activityDefinition.code) === IV_THERAPY_APPOINTMENT_TYPE_CODE;

  const practitionerRefs = extractPractitionersFromAppointment(appointment);
  const primaryPractitionerRef = practitionerRefs[0];
  const provider: Practitioner | null =
    primaryPractitionerRef != null
      ? isResource(primaryPractitionerRef, 'Practitioner')
        ? (primaryPractitionerRef as Practitioner)
        : await medplum.readReference(primaryPractitionerRef as Reference<Practitioner>)
      : null;

  const partnerRef = extractPartnerFromAppointment(appointment);
  const partner: Organization | null = partnerRef != null ? await medplum.readReference(partnerRef) : null;

  const guests: GuestFormData[] = [];
  if (isIvTherapy) {
    const patientRefs = extractPatientsFromAppointment(appointment);
    for (const ref of patientRefs) {
      const patient = isResource(ref, 'Patient')
        ? (ref as Patient)
        : await medplum.readReference(ref as Reference<Patient>);
      if (patient) guests.push(patient);
    }
  } else {
    const patientRefs = extractPatientsFromAppointment(appointment);
    for (const ref of patientRefs) {
      const patient = isResource(ref, 'Patient')
        ? (ref as Patient)
        : await medplum.readReference(ref as Reference<Patient>);
      if (patient) guests.push(patient);
    }
    const practitionerRefsForGuests = practitionerRefs.slice(1);
    for (const ref of practitionerRefsForGuests) {
      const practitionerResource = isResource(ref, 'Practitioner')
        ? (ref as Practitioner)
        : await medplum.readReference(ref as Reference<Practitioner>);
      if (practitionerResource) {
        const role = await medplum.searchOne('PractitionerRole', {
          practitioner: `Practitioner/${practitionerResource.id}`,
        });
        if (role) guests.push(role);
      }
    }
  }

  const encounterClass = getExtensionValue(appointment, SCHEDULING_PARAMETERS_EXTENSION, 'encounterClass') as
    | Coding
    | undefined;
  const visitType =
    encounterClass?.code === AMBULATORY_ENCOUNTER_CLASS_CODING.code
      ? AMBULATORY_ENCOUNTER_CLASS_CODING
      : VIRTUAL_ENCOUNTER_CLASS_CODING;

  const locationRefs = extractActorsFromAppointment<Reference<Location>>(appointment, 'Location');
  const locationRef = locationRefs[0];
  const location: Location | null = locationRef != null ? await medplum.readReference(locationRef) : null;

  const timezone =
    (getExtensionValue(appointment, SCHEDULING_PARAMETERS_EXTENSION, 'timezone') as string | undefined) ??
    getLocationTimezone(location);

  // Parse UTC instant and interpret date/time in the appointment's timezone so the form shows
  // the correct local time. Backend stores UTC; save sends date + startTime + timezone.
  const startInTz =
    appointment.start && timezone
      ? dayjs.utc(appointment.start).tz(timezone)
      : appointment.start
        ? dayjs.utc(appointment.start)
        : null;
  const date = startInTz ? new Date(startInTz.year(), startInTz.month(), startInTz.date()) : null;
  const startTime = startInTz ? startInTz.format('HH:mm') : '';

  const endInTz =
    appointment.end && timezone
      ? dayjs.utc(appointment.end).tz(timezone)
      : appointment.end
        ? dayjs.utc(appointment.end)
        : null;
  const endTime = endInTz ? endInTz.format('HH:mm') : '';

  let notesJson: object | null = null;
  if (appointment.description) {
    try {
      notesJson = JSON.parse(appointment.description) as object;
    } catch {
      notesJson = null;
    }
  }

  const virtualServiceType = getExtensionValue(
    appointment,
    APPOINTMENT_CUSTOM_INFO_EXTENSION,
    'virtual-service-type'
  ) as 'video' | 'phone' | undefined;
  const callType: 'video' | 'phone' | null =
    visitType.code === VIRTUAL_ENCOUNTER_CLASS_CODING.code ? (virtualServiceType ?? 'video') : null;
  const videoLink = extractVirtualServiceUrlFromAppointment(appointment) ?? '';
  const phoneNumber = extractVirtualServicePhoneNumberFromAppointment(appointment) ?? '';

  return {
    appointmentType: activityDefinition ?? null,
    appointmentTypeCode: appointmentTypeCode ?? null,
    provider,
    partner,
    guests,
    visitType,
    location,
    timezone,
    callType,
    videoLink,
    videoLinkType: 'custom',
    phoneNumber,
    date,
    startTime,
    endTime,
    notes: appointment.comment ?? '',
    notesJson,
    appointmentReference: getReferenceString(appointment) ?? null,
    editScope: 'single',
    isRepeating: false,
    recurrencePreset: 'none',
    repeatingInterval: AppointmentRepeatingInterval.WEEKLY,
    repeatTimes: 1,
    monthlyNth: 1,
    monthlyWeekday: DEFAULT_MONTHLY_RECURRENCE_WEEKDAY,
  };
}
